import { Link } from "react-router-dom";
import type { CommunityTopic } from "@/mocks/community";

interface TopicRankListProps {
  topics: CommunityTopic[];
}

function rankStyle(rank: number) {
  if (rank === 1) return "bg-primary-500 text-background-50";
  if (rank === 2) return "bg-primary-400 text-background-50";
  if (rank === 3) return "bg-accent-500 text-primary-950";
  return "bg-background-200 text-foreground-700";
}

const trendIcon: Record<CommunityTopic["trend"], string> = {
  up: "ri-arrow-up-line text-primary-500",
  down: "ri-arrow-down-line text-secondary-500",
  new: "ri-flashlight-fill text-accent-600",
};

export default function TopicRankList({ topics }: TopicRankListProps) {
  const maxHeat = topics.reduce((max, topic) => Math.max(max, topic.heat), 1);

  if (topics.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-background-300 bg-background-50 px-6 py-16 text-center">
        <i className="ri-search-eye-line text-3xl text-foreground-400" aria-hidden="true" />
        <p className="mt-3 text-sm text-foreground-600">没有匹配到话题，换个板块或关键词试试。</p>
      </div>
    );
  }

  return (
    <ol className="space-y-3">
      {topics.map((topic) => (
        <li key={topic.rank}>
          <Link
            to={`/post/${topic.postId}`}
            className="group block rounded-xl border border-background-200 bg-background-50 p-4 transition-colors hover:border-primary-300 md:p-5"
          >
            <div className="flex items-start gap-4">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg font-heading text-base font-bold ${rankStyle(
                  topic.rank,
                )}`}
              >
                {topic.rank}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="rounded bg-secondary-100 px-2 py-0.5 text-xs font-medium text-secondary-900">
                    {topic.categoryName}
                  </span>
                  {topic.trend === "new" && (
                    <span className="rounded bg-accent-100 px-2 py-0.5 text-xs font-medium text-accent-900">新话题</span>
                  )}
                  <i className={`${trendIcon[topic.trend]} text-xs`} aria-hidden="true" />
                </div>
                <h3 className="mt-2 text-base font-semibold leading-snug text-foreground-950 transition-colors group-hover:text-primary-600 md:text-lg">
                  {topic.title}
                </h3>
                <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2">
                  <span className="text-xs text-foreground-500">
                    <i className="ri-fire-line mr-1 text-primary-500" aria-hidden="true" />
                    热度 {topic.heat.toLocaleString("zh-CN")}
                  </span>
                  <span className="text-xs text-foreground-500">
                    <i className="ri-chat-3-line mr-1" aria-hidden="true" />
                    {topic.replyCount} 条讨论
                  </span>
                </div>
                <div className="mt-3 h-1.5 w-full overflow-hidden rounded-full bg-background-200">
                  <div
                    className="h-full rounded-full bg-gradient-to-r from-primary-500 to-accent-500"
                    style={{ width: `${Math.round((topic.heat / maxHeat) * 100)}%` }}
                  />
                </div>
              </div>
            </div>
          </Link>
        </li>
      ))}
    </ol>
  );
}